import {
  AfterContentInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Directive,
  ElementRef,
  EventEmitter,
  Host,
  HostListener,
  Input,
  isDevMode,
  Optional,
  Output,
  ViewChild,
  ViewEncapsulation
} from '@angular/core';
import { SimDateAdapter } from '@ngx-simple/core/datetime';
import { isObject } from '@ngx-simple/core/typeof';
import { createMissingDateImplError } from './datepicker-errors';
import { DateFilterFn } from './datepicker-input-base';

interface ItemStep {
  hours: number;
  minutes: number;
  seconds: number;
}

@Directive({
  selector: '[timeItemList]',
  host: {
    class: 'sim-time-picker-column',
    '[class.sim-time-picker-hourList]': 'timeItemList === "hourList"',
    '[class.sim-time-picker-minuteList]': 'timeItemList === "minuteList"',
    '[class.sim-time-picker-secondList]': 'timeItemList === "secondList"',
    '[class.sim-time-picker-item-active]': 'timeItemList === "isActive"',
    '[attr.tabIndex]': '0'
  }
})
export class SimTimeItemListDirective<D> {
  isActive: boolean;

  @Input() timeItemList: 'hourList' | 'minuteList' | 'secondList' | 'ampmList';

  constructor(@Host() public timeView: SimTimeViewComponent<D>, private elementRef: ElementRef) {}

  @HostListener('focus')
  onFocus() {
    this.isActive = true;
  }

  @HostListener('blur')
  onBlur() {
    this.isActive = false;
  }

  private nextItem(): void {
    this.timeView.nextItem(this.timeItemList);
  }

  private prevItem(): void {
    this.timeView.prevItem(this.timeItemList);
  }

  @HostListener('keydown.arrowdown', ['$event'])
  onKeydownArrowDown(event: KeyboardEvent) {
    event.preventDefault();

    this.nextItem();
  }

  @HostListener('keydown.arrowup', ['$event'])
  onKeydownArrowUp(event: KeyboardEvent) {
    event.preventDefault();

    this.prevItem();
  }

  @HostListener('keydown.arrowright', ['$event'])
  onKeydownArrowRight(event: KeyboardEvent) {
    event.preventDefault();

    const listName = (event.target as HTMLElement).className;

    if (listName.indexOf('hourList') !== -1) {
      this.timeView.minuteList.nativeElement.focus();
    } else if (listName.indexOf('minuteList') !== -1 && this.timeView._secondItems.length !== 0) {
      this.timeView.secondList.nativeElement.focus();
    }
  }

  @HostListener('keydown.arrowleft', ['$event'])
  onKeydownArrowLeft(event: KeyboardEvent) {
    event.preventDefault();

    const listName = (event.target as HTMLElement).className;

    if (listName.indexOf('minuteList') !== -1) {
      this.timeView.hourList.nativeElement.focus();
    } else if (listName.indexOf('secondList') !== -1) {
      this.timeView.minuteList.nativeElement.focus();
    }
  }

  @HostListener('keydown.enter', ['$event'])
  onKeydownEnter(event: KeyboardEvent) {
    event.preventDefault();

    this.timeView.okButtonClick();
  }

  @HostListener('keydown.escape', ['$event'])
  onKeydownEscape(event: KeyboardEvent) {
    event.preventDefault();
    this.timeView.cancelButtonClick();
  }

  @HostListener('mouseover')
  onHover() {
    this.elementRef.nativeElement.focus();
  }

  @HostListener('click', ['$event'])
  onClick(event: Event) {
    event.preventDefault();
    event.stopPropagation();
    const time = (event.target as HTMLElement).dataset.time;
    if (!time) {
      return;
    }
    this.timeView.scrollIntoView(time, this.timeItemList);
    return false;
  }

  @HostListener('wheel', ['$event'])
  onScroll(event: WheelEvent) {
    event.preventDefault();
    event.stopPropagation();

    if (event.deltaY > 0) {
      this.nextItem();
    } else if (event.deltaY < 0) {
      this.prevItem();
    }
  }
}

@Component({
  selector: 'sim-time-view',
  templateUrl: './time-view.component.html',
  styleUrls: ['./datepicker.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SimTimeViewComponent<D> implements AfterContentInit {
  // _init: boolean = false;
  /**
   * By default `format` is set to hh:mm tt. <br>
   * List of time-flags: <br>
   * `HH` : hours field in 24-hours format with leading zero <br>
   * `mm` : minutes field with leading zero <br>
   * `ss` : seconds field with leading zero <br>
   */
  @Input() format: string = 'HH:mm:ss';
  @Input() spinLoop: boolean = true;

  @Input()
  set activeDate(value: D) {
    if (value) {
      this._generateHours();
      this._generateMinutes();
      this._generateSeconds();

      const sections = this._formatTime(value, this.format);
      if (sections.length) {
        const scrollIntoViews = [];
        if (sections[0] !== '-1') {
          this.selectedHour = sections[0];
          this._updateHourView(0, 7);
          scrollIntoViews.push({
            selected: this.selectedHour,
            timeItemList: 'hourList'
          });
        }
        if (sections[1] !== '-1') {
          this.selectedMinute = sections[1];
          this._updateMinuteView(0, 7);
          scrollIntoViews.push({
            selected: this.selectedMinute,
            timeItemList: 'minuteList'
          });
        }
        if (sections[2] !== '-1') {
          this.selectedSecond = sections[2];
          this._updateSecondView(0, 7);
          scrollIntoViews.push({
            selected: this.selectedSecond,
            timeItemList: 'secondList'
          });
        }
        this.initScrollIntoView(scrollIntoViews);
        // if (!this._init) {
        //   this._init = true;
        //   setTimeout(() => {
        //     this[scrollIntoViews[0].timeItemList].nativeElement.focus();
        //   });
        // }
      }
    }
    this._activeDate = value;
  }
  get activeDate(): D {
    return this._activeDate;
  }
  private _activeDate: D;

  /** 当前选定的日期 */
  @Input()
  get selected(): D | null {
    return this._selected;
  }
  set selected(value: D | null) {
    this._selected = value;
  }
  private _selected: D;

  /** 最小可选日期. */
  @Input()
  get minDate(): D | null {
    return this._minDate;
  }
  set minDate(value: D | null) {
    this._minDate = this._dateAdapter.getValidDateOrNull(this._dateAdapter.deserialize(value));
  }
  private _minDate: D | null;

  /** 最大可选日期. */
  @Input()
  get maxDate(): D | null {
    return this._maxDate;
  }
  set maxDate(value: D | null) {
    this._maxDate = this._dateAdapter.getValidDateOrNull(this._dateAdapter.deserialize(value));
  }
  private _maxDate: D | null;

  /** 用于过滤哪些日期可选的函数 */
  @Input() dateFilter: DateFilterFn<D | null>;

  @Input()
  set itemStep(value: ItemStep) {
    if (isObject(value)) {
      this._itemStep = Object.assign(this._itemStep, value);
      this._generateHours();
      this._generateMinutes();
      this._generateSeconds();
    }
  }
  get itemStep(): ItemStep {
    return this._itemStep;
  }
  _itemStep: ItemStep = { hours: 1, minutes: 1, seconds: 1 };

  @Output() readonly selectedChange: EventEmitter<Date> = new EventEmitter<Date>();

  _hourItems = [];
  _minuteItems = [];
  _secondItems = [];

  private _hourView = [];
  private _minuteView = [];
  private _secondView = [];

  selectedHour: string;
  selectedMinute: string;
  selectedSecond: string;

  private _isHourListLoop = this.spinLoop;
  private _isMinuteListLoop = this.spinLoop;
  private _isSecondListLoop = this.spinLoop;

  @ViewChild('hourList')
  hourList: ElementRef;

  @ViewChild('minuteList')
  minuteList: ElementRef;

  @ViewChild('secondList')
  secondList: ElementRef;

  constructor(private _changeDetectorRef: ChangeDetectorRef, @Optional() public _dateAdapter: SimDateAdapter<D>) {
    if (!this._dateAdapter && isDevMode) {
      throw createMissingDateImplError('SimDateAdapter');
    }
    this._activeDate = this._dateAdapter.today();
  }

  ngAfterContentInit() {
    this._init();
  }

  _init() {}

  okButtonClick() {
    this.selectedChange.next(this._getSelectedTime());
  }

  cancelButtonClick() {}

  get hourView(): string[] {
    return this._hourView;
  }

  get minuteView(): string[] {
    return this._minuteView;
  }

  get secondView(): string[] {
    return this._secondView;
  }

  /** 初始化滚动列 */
  initScrollIntoView(timeItemList: any[]) {
    timeItemList.forEach(item => {
      this._scrollIntoView(item.selected, item.timeItemList);
    });
  }

  private _scrollIntoView(time: string, timeItemList: string) {
    switch (timeItemList) {
      case 'hourList':
        const hourIntoView = this._scrollItemIntoView(time, this._hourItems, this.selectedHour, this._isHourListLoop, 'hour');
        if (hourIntoView) {
          this._hourView = hourIntoView.view;
          this.selectedHour = hourIntoView.selectedItem;
        }
        break;
      case 'minuteList':
        const minuteIntoView = this._scrollItemIntoView(time, this._minuteItems, this.selectedMinute, this._isMinuteListLoop, 'minute');
        if (minuteIntoView) {
          this._minuteView = minuteIntoView.view;
          this.selectedMinute = minuteIntoView.selectedItem;
        }
        break;
      case 'secondList':
        const secondIntoView = this._scrollItemIntoView(time, this._secondItems, this.selectedSecond, this._isSecondListLoop, 'second');
        if (secondIntoView) {
          this._secondView = secondIntoView.view;
          this.selectedSecond = secondIntoView.selectedItem;
        }
        break;
      default:
        throw Error('timeItemList type not `hourList | minuteList | secondList`');
    }
  }

  scrollIntoView(time: string, timeItemList: string) {
    this._scrollIntoView(time, timeItemList);
    this.selectedChange.next(this._getSelectedTime());
  }

  nextItem(timeItemList: string) {
    switch (timeItemList) {
      case 'hourList':
        const nextHour = this._nextItem(this._hourItems, this.selectedHour, this._isHourListLoop, 'hour');
        if (nextHour) {
          this._hourView = nextHour.view;
          this.selectedHour = nextHour.selectedItem;
        }
        break;
      case 'minuteList':
        const nextMinute = this._nextItem(this._minuteItems, this.selectedMinute, this._isMinuteListLoop, 'minute');
        if (nextMinute) {
          this._minuteView = nextMinute.view;
          this.selectedMinute = nextMinute.selectedItem;
        }
        break;
      case 'secondList':
        const nextSecond = this._nextItem(this._secondItems, this.selectedSecond, this._isSecondListLoop, 'second');
        if (nextSecond) {
          this._secondView = nextSecond.view;
          this.selectedSecond = nextSecond.selectedItem;
        }
        break;
      default:
        throw Error('timeItemList type not `hourList | minuteList | secondList`');
    }
    this.selectedChange.next(this._getSelectedTime());
  }

  prevItem(timeItemList: string) {
    switch (timeItemList) {
      case 'hourList':
        const prevHour = this._prevItem(this._hourItems, this.selectedHour, this._isHourListLoop, 'hour');
        if (prevHour) {
          this._hourView = prevHour.view;
          this.selectedHour = prevHour.selectedItem;
        }
        break;
      case 'minuteList':
        const prevMinute = this._prevItem(this._minuteItems, this.selectedMinute, this._isMinuteListLoop, 'minute');
        if (prevMinute) {
          this._minuteView = prevMinute.view;
          this.selectedMinute = prevMinute.selectedItem;
        }
        break;
      case 'secondList':
        const prevSecond = this._prevItem(this._secondItems, this.selectedSecond, this._isSecondListLoop, 'second');
        if (prevSecond) {
          this._secondView = prevSecond.view;
          this.selectedSecond = prevSecond.selectedItem;
        }
        break;
      default:
        throw Error('timeItemList type not `hourList | minuteList | secondList`');
    }
    this.selectedChange.next(this._getSelectedTime());
  }

  private _updateHourView(start: any, end: any): void {
    this._hourView = this._viewToString(this._hourItems.slice(start, end), 'hour');
  }

  private _updateMinuteView(start: any, end: any): void {
    this._minuteView = this._viewToString(this._minuteItems.slice(start, end), 'minute');
  }

  private _updateSecondView(start: any, end: any): void {
    this._secondView = this._viewToString(this._secondItems.slice(start, end), 'second');
  }

  private _addEmptyItems(items: string[]): void {
    for (let i = 0; i < 3; i++) {
      items.push(null);
    }
  }

  private _generateHours(): void {
    let hourItemsCount = 24;
    if (this.format.indexOf('h') !== -1) {
      hourItemsCount = 13;
    }
    hourItemsCount /= this.itemStep.hours;

    let i = this.format.indexOf('H') !== -1 ? 0 : 1;

    if (hourItemsCount < 7 || !this.spinLoop) {
      this._addEmptyItems(this._hourItems);
      this._isHourListLoop = false;
    }

    if (hourItemsCount > 1) {
      for (i; i < hourItemsCount; i++) {
        this._hourItems.push(i * this.itemStep.hours);
      }
    } else {
      this._hourItems.push(0);
    }

    if (hourItemsCount < 7 || !this.spinLoop) {
      this._addEmptyItems(this._hourItems);
    }
  }

  private _generateMinutes(): void {
    const minuteItemsCount = 60 / this.itemStep.minutes;

    if (minuteItemsCount < 7 || !this.spinLoop) {
      this._addEmptyItems(this._minuteItems);
      this._isMinuteListLoop = false;
    }

    for (let i = 0; i < minuteItemsCount; i++) {
      this._minuteItems.push(i * this.itemStep.minutes);
    }

    if (minuteItemsCount < 7 || !this.spinLoop) {
      this._addEmptyItems(this._minuteItems);
    }
  }

  private _generateSeconds(): void {
    const secondItemsCount = 60 / this.itemStep.seconds;

    if (secondItemsCount < 7 || !this.spinLoop) {
      this._addEmptyItems(this._secondItems);
      this._isSecondListLoop = false;
    }

    for (let i = 0; i < secondItemsCount; i++) {
      this._secondItems.push(i * this.itemStep.seconds);
    }

    if (secondItemsCount < 7 || !this.spinLoop) {
      this._addEmptyItems(this._secondItems);
    }
  }

  private _prevItem(items: any[], selectedItem: string, isListLoop: boolean, viewType: string): any {
    const selectedIndex = items.indexOf(parseInt(selectedItem, 10));
    const itemsCount = items.length;
    let view;

    if (selectedIndex === -1) {
      view = items.slice(0, 7);
      selectedItem = items[3];
    } else if (isListLoop) {
      if (selectedIndex - 4 < 0) {
        view = items.slice(itemsCount - (4 - selectedIndex), itemsCount);
        view = view.concat(items.slice(0, selectedIndex + 3));
      } else if (selectedIndex + 4 > itemsCount) {
        view = items.slice(selectedIndex - 4, itemsCount);
        view = view.concat(items.slice(0, selectedIndex + 3 - itemsCount));
      } else {
        view = items.slice(selectedIndex - 4, selectedIndex + 3);
      }

      selectedItem = selectedIndex === 0 ? items[itemsCount - 1] : items[selectedIndex - 1];
    } else if (selectedIndex > 3) {
      view = items.slice(selectedIndex - 4, selectedIndex + 3);
      selectedItem = items[selectedIndex - 1];
    } else if (selectedIndex === 3) {
      view = items.slice(0, 7);
    }
    view = this._viewToString(view, viewType);
    selectedItem = this._itemToString(selectedItem, viewType);
    return {
      selectedItem,
      view
    };
  }

  private _nextItem(items: any[], selectedItem: string, isListLoop: boolean, viewType: string): any {
    const selectedIndex = items.indexOf(parseInt(selectedItem, 10));
    const itemsCount = items.length;
    let view;

    if (selectedIndex === -1) {
      view = items.slice(0, 7);
      selectedItem = items[3];
    } else if (isListLoop) {
      if (selectedIndex < 2) {
        view = items.slice(itemsCount - (2 - selectedIndex), itemsCount);
        view = view.concat(items.slice(0, selectedIndex + 5));
      } else if (selectedIndex + 4 >= itemsCount) {
        view = items.slice(selectedIndex - 2, itemsCount);
        view = view.concat(items.slice(0, selectedIndex + 5 - itemsCount));
      } else {
        view = items.slice(selectedIndex - 2, selectedIndex + 5);
      }

      selectedItem = selectedIndex === itemsCount - 1 ? items[0] : items[selectedIndex + 1];
    } else if (selectedIndex + 1 < itemsCount - 3) {
      view = items.slice(selectedIndex - 2, selectedIndex + 5);
      selectedItem = items[selectedIndex + 1];
    } else if (selectedIndex === itemsCount - 4) {
      view = items.slice(selectedIndex - 3, itemsCount);
    }
    view = this._viewToString(view, viewType);
    selectedItem = this._itemToString(selectedItem, viewType);
    return {
      selectedItem,
      view
    };
  }

  private _scrollItemIntoView(item: string, items: any[], selectedItem: string, isListLoop: boolean, viewType: string): any {
    let itemIntoView;
    if (items) {
      const index = item === '上午' || item === '下午' ? items.indexOf(item) : items.indexOf(parseInt(item, 10));
      let view;

      if (index !== -1) {
        if (isListLoop) {
          if (index > 0) {
            selectedItem = this._itemToString(items[index - 1], viewType);
            itemIntoView = this._nextItem(items, selectedItem, isListLoop, viewType);
          } else {
            selectedItem = this._itemToString(items[1], viewType);
            itemIntoView = this._prevItem(items, selectedItem, isListLoop, viewType);
          }
        } else {
          view = items.slice(index - 3, index + 4);
          selectedItem = this._itemToString(items[index], viewType);
          itemIntoView = { selectedItem, view };
        }
        itemIntoView.view = this._viewToString(itemIntoView.view, viewType);
      }
    }
    return itemIntoView;
  }

  private _getSelectedTime(): Date {
    const date = new Date();
    date.setHours(parseInt(this.selectedHour, 10) || 0, parseInt(this.selectedMinute, 10) || 0, parseInt(this.selectedSecond, 10) || 0);
    return date;
  }

  private _itemToString(item: any, viewType: string): string {
    if (item === null) {
      item = '';
    } else if (viewType && typeof item !== 'string') {
      const leadZeroHour = item < 10 && (this.format.indexOf('hh') !== -1 || this.format.indexOf('HH') !== -1);
      const leadZeroMinute = item < 10 && this.format.indexOf('mm') !== -1;

      const leadZero = viewType === 'hour' ? leadZeroHour : leadZeroMinute;
      item = leadZero ? '0' + item : `${item}`;
    }
    return item;
  }

  private _viewToString(view: any, viewType: string): any {
    for (let i = 0; i < view.length; i++) {
      if (typeof view[i] !== 'string') {
        view[i] = this._itemToString(view[i], viewType);
      }
    }
    return view;
  }

  /**
   * 格式化时间
   */
  private _formatTime(value: D, format: string): string[] {
    /** 处理显示字段 */
    const formats = ['HH:mm:ss', 'HH:mm', 'mm:ss', 'HH', 'mm', 'ss'].filter(str => format.indexOf(str) > -1);
    let _format = 'HH:mm:ss';
    if (formats.length) {
      _format = formats[0];
    }

    if (!value) {
      return [];
    } else {
      const hour = this._dateAdapter.getHours(value);
      const minute = this._dateAdapter.getMinutes(value);
      const second = this._dateAdapter.getSeconds(value);
      let formattedMinute: string;
      let formattedSecond: string;
      let formattedHour: string;

      formattedHour = _format.indexOf('HH') === -1 ? '-1' : ('00' + hour).slice(-2);

      formattedMinute = _format.indexOf('mm') === -1 ? '-1' : ('00' + minute).slice(-2);

      formattedSecond = _format.indexOf('ss') === -1 ? '-1' : ('00' + second).slice(-2);

      return [formattedHour, formattedMinute, formattedSecond];
    }
  }
}
